import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { Col, Row, Table, Button, Form, Spin, notification } from "antd";
import { AppstoreFilled } from '@ant-design/icons';

import { getTasks, getTasksByCode, updateTask, getTaskStatus, createTaskComment } from "../../API/tasks";
import TasksSearch from "./components/TasksSearch/TasksSearch";
import Columns from './components/TasksTable/columns';
import TasksModal from "./components/TasksModal/TasksModal";

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';
import dayjs from "dayjs";

import { useAuth } from "../Context/AuthContext";

const Tasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Permisos
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;
  const canTasksRead = userData ? hasPermission("tasks", "read") : false;

  // Estados componente
  const [api, contextHolder] = notification.useNotification();
  const [tasks, setTasks] = useState([]);
  const [taskStatus, setTaskStatus] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [form] = Form.useForm();
  
  // Loader
  const [loading, setLoading] = useState(true);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');
  
  // Filtros y parámetros
  const [searchParams] = useSearchParams();
  const dashboardStatusFilter = searchParams.get('status');
  const dashboardConditionFilter = searchParams.get('condition');
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  
  // Nuevos estados para filtros en cascada
  const [activeFilters, setActiveFilters] = useState({});
  
  // Detectar si los filtros vienen del dashboard
  const hasUrlFilters = dashboardStatusFilter || dashboardConditionFilter || assetsFromUrl.length > 0 || areasFromUrl.length > 0;

  // Sincronizar filtros del dashboard con activeFilters
  useEffect(() => {
    const initialFilters = {};
    
    if (dashboardStatusFilter) {
      initialFilters.status = [dashboardStatusFilter];
    }
    
    if (dashboardConditionFilter) {
      initialFilters.condition = [dashboardConditionFilter];
    }
    
    if (assetsFromUrl.length > 0) {
      initialFilters.asset = assetsFromUrl;
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.area = areasFromUrl;
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [dashboardStatusFilter, dashboardConditionFilter, assetsFromUrl.join(','), areasFromUrl.join(',')]);

  // Cargar estados de tareas
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const response = await getTaskStatus();
        setTaskStatus(response || []);
      } catch (error) {
        api.error({ message: "Error al cargar los estados de tareas" });
      }
    };

    fetchTaskStatus();
  }, []);

  // Cargar lista de tareas
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await getTasks();
      setTasks(response);
      setFilteredData(response);
    } catch {
      api.error({ message: "Error al cargar las tareas"});
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros a los datos
  const applyFilters = useCallback((data, filters) => {
    let filtered = [...data];
    
    Object.entries(filters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      
      const filterMap = {
        'status': (task) => task.status?.name || '',
        'condition': (task) => task.condition || '',
        'asset': (task) => task.gap?.dependency_control?.dependency?.asset?.name || '',
        'area': (task) => task.gap?.area?.name || '',
        'responsible_area': (task) => task.responsible_area?.name || '',
        'dependency': (task) => task.gap?.dependency_control?.dependency?.name || '',
        'type': (task) => task.gap?.type || '',
      };
      
      const getter = filterMap[key];
      if (!getter) return;
      
      filtered = filtered.filter(task => {
        const value = getter(task);
        return values.includes(value);
      });
    });
    
    return filtered;
  }, []);

  // Actualizar filteredData cuando cambien los filtros o las tareas
  useEffect(() => {
    const filtered = applyFilters(tasks, activeFilters);
    setFilteredData(filtered);
  }, [tasks, activeFilters, applyFilters]);

  // Guardar tarea 
  const handleSave = async (values) => {
    try {
      if (editingTask) {
        const payload = {
          name: values.name,
          description: values.description,
          status_id: values.status,
          responsible_area_id: editingTask.responsible_area?.id,
          date_committed: values.date_committed?.toISOString() || null,
          date_finished: values.date_finished?.toISOString() || null,
        };

        await updateTask(editingTask.code, payload);
        
        if (newComment.trim()) {
          try {
            const commentData = {
              task_code: editingTask.code,
              comment: newComment.trim()
            };

            const commentResponse = await createTaskComment(commentData);

            const newCommentObj = {
              id: commentResponse.id,
              text: commentResponse.comment,
              date: new Date().toISOString(),
              user: `${commentResponse.user.first_name} ${commentResponse.user.last_name}`,
              username: commentResponse.user.username,
            };

            setComments(prev => [...prev, newCommentObj]);
            setNewComment('');
          } catch (error) {
            console.error("Error creando comentario desde Guardar:", error);
            api.error({ message: "Error al guardar el comentario pendiente" });
          }
        }
        api.success({ message: "Tarea actualizada."});
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingTask(null);
      setComments([]);
      setNewComment('');
      fetchTasks();
    } catch (error) {
      api.error({ 
        message: "Error al guardar la tarea.",
        description: error?.response?.data?.name?.[0].includes("already exists") 
          ? "Ya existe una tarea con ese nombre."
          : "Ocurrió un error inesperado al guardar la tarea.",
        key: 'save-tasks-error'
      });
    }
  };

  // Mostrar modal para editar
  const showModal = async (task = null) => {
    if (task) {
      try {
        setLoading(true);
        const fullTask = await getTasksByCode(task.code);

        form.setFieldsValue({
          name: fullTask.name,
          description: fullTask.description,
          status: fullTask?.status?.id,
          responsible_area: fullTask?.responsible_area?.name,
          date_committed: fullTask.date_committed ? dayjs(fullTask.date_committed) : null,
          date_finished: fullTask.date_finished ? dayjs(fullTask.date_finished) : null,
        });

        const formattedComments = (fullTask.comments || []).map(comment => ({
          id: comment.id,
          text: comment.comment,
          date: comment.created_at || new Date().toISOString(),
          user: comment.user ? `${comment.user.first_name} ${comment.user.last_name}` : 'Sistema',
          username: comment.user?.username || 'unknown',
          created: comment.created
        }));

        setComments(formattedComments);
        setEditingTask(fullTask);
      } catch (error) {
        api.error({ message: "Error al cargar datos de la tarea." });
      } finally {
        setLoading(false);
      }
    } else {
      setEditingTask(null);
      form.resetFields();
    }

    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const tasksTableFilter = () => {
    // Función helper para aplicar filtros
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        'status': (task) => task.status?.name || '',
        'condition': (task) => task.condition || '',
        'asset': (task) => task.gap?.dependency_control?.dependency?.asset?.name || '',
        'area': (task) => task.gap?.area?.name || '',
        'responsible_area': (task) => task.responsible_area?.name || '',
        'dependency': (task) => task.gap?.dependency_control?.dependency?.name || '',
        'type': (task) => task.gap?.type || '',
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(task => {
        const value = getter(task);
        return filterValues.includes(value);
      });
    };

    // Obtener datos filtrados excluyendo una columna
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...tasks];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones dinámicas basadas en filtros activos
    const assetsData = getFilteredDataForColumn('asset');
    const taskAssets = Array.from(
      new Map(
        assetsData
          .map(task => [task.gap?.dependency_control?.dependency?.asset?.name, task.gap?.dependency_control?.dependency?.asset])
          .filter(([_, asset]) => asset && asset.name)
      ).values()
    );

    const statusesData = getFilteredDataForColumn('status');
    const taskStatuses = Array.from(
      new Map(
        statusesData
          .map(task => [task.status?.index, task.status])
          .filter(([status]) => status)
      ).values()
    );

    const responsibleAreasData = getFilteredDataForColumn('responsible_area');
    const taskResponsibleAreas = Array.from(
      new Map(
        responsibleAreasData
          .map(task => [task.responsible_area?.id, task.responsible_area])
          .filter(([area]) => area)
      ).values()
    );

    const conditionsData = getFilteredDataForColumn('condition');
    const taskConditions = Array.from(
      new Set(
        conditionsData
          .map(task => task.condition)
          .filter(Boolean)
      )
    );

    const dependenciesData = getFilteredDataForColumn('dependency');
    const taskDependencies = Array.from(
      new Set(
        dependenciesData
          .map(task => task.gap?.dependency_control?.dependency?.name)
          .filter(Boolean)
      )
    );

    const typesData = getFilteredDataForColumn('type');
    const taskTypes = Array.from(
      new Set(
        typesData
          .map(task => task.gap?.type)
          .filter(Boolean)
      )
    );

    const areasData = getFilteredDataForColumn('area');
    const taskAreas = Array.from(
      new Map(
        areasData
          .map(task => [task.gap?.area?.name, task.gap?.area])
          .filter(([name]) => name)
      ).values()
    );

    return {
      assets: taskAssets.map(asset => ({ name: asset.name })),
      statuses: taskStatuses.map(status => ({ id: status.index, name: status.name })),
      responsibleAreas: taskResponsibleAreas.map(area => ({ id: area.id, name: area.name })),
      conditions: taskConditions.map(name => ({ name })),
      dependencies: taskDependencies.map(name => ({ name })),
      types: taskTypes.map(name => ({ name })),
      areas: taskAreas.map(area => ({ name: area.name })),
      dashboardStatusFilter: dashboardStatusFilter || null,
      dashboardConditionFilter: dashboardConditionFilter || null,
      dashboardAssetsFilter: assetsFromUrl,
      dashboardAreasFilter: areasFromUrl,
    };
  };

  // Manejar cambios en los filtros de la tabla
  const handleTableChange = (pagination, filters, sorter, extra) => {
    // Guardar filtros activos
    setActiveFilters(filters);
    
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();
      
      if (filters.status?.length > 0) {
        newParams.set('status', filters.status[0]);
      }
      
      if (filters.condition?.length > 0) {
        newParams.set('condition', filters.condition[0]);
      }
      
      if (filters.asset?.length > 0) {
        newParams.set('assets', filters.asset.join(','));
      }
      
      if (filters.area?.length > 0) {
        newParams.set('areas', filters.area.join(','));
      }
      
      // Preservar otros parámetros si existen
      const currentParams = new URLSearchParams(location.search);
      currentParams.forEach((value, key) => {
        if (!['status', 'condition', 'assets', 'areas'].includes(key)) {
          newParams.set(key, value);
        }
      });
      
      navigate({
        pathname: '/tasks',
        search: newParams.toString() ? `?${newParams.toString()}` : ''
      }, { replace: true });
    }
  };

  const handleAddComment = async () => {
    if (newComment.trim()) {
      try {
        setIsSubmitting(true);
        
        const commentData = {
          task_code: editingTask.code,
          comment: newComment
        };
  
        const response = await createTaskComment(commentData);
        
        const newCommentObj = {
          id: response.id,
          text: response.comment,
          date: new Date().toISOString(),
          user: `${response.user.first_name} ${response.user.last_name}`,
          username: response.user.username,
        };
  
        setComments(prev => [...prev, newCommentObj]);
        setNewComment('');
        api.success({ message: 'Comentario agregado correctamente' });
      } catch (error) {
        api.error({ message: 'Error al agregar el comentario' });
        console.error('Error adding comment:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canTasksRead}
      redirectTo="/home"
    >
      <>
      {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start">
              <Col>
                <PageTitle title="Tareas" icon="tasks" />
              </Col>
            </Row>

            <Row justify="start" gutter={[0, 16]}>
              <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TasksSearch
                  tasksData={tasks}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 0]}>
              {
                isSuperuser &&
                <Col>
                  <Button 
                    type="primary"
                    className="view-deleted-button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (dashboardStatusFilter) params.set('status', dashboardStatusFilter);
                      if (dashboardConditionFilter) params.set('condition', dashboardConditionFilter);
                      if (assetsFromUrl.length) params.set('assets', assetsFromUrl.join(','));
                      if (areasFromUrl.length) params.set('areas', areasFromUrl.join(','));
                      navigate(`/tasks-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                    }}>
                    <AppstoreFilled />
                    Ver Borrados
                  </Button>
                </Col>
              }
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Tareas.csv"
                  transformData={(data) =>
                    data.map(item => ({
                      'Nombre': item.name || '',
                      'Estado': item.status?.name || '',
                      'Responsable Área': item.responsible_area?.name || '',
                      'Condición': item.condition || '',
                      'Fecha Comprometida': item.date_committed ? dayjs(item.date_committed).format('DD/MM/YYYY') : 'No especificada',
                      'Activo': item.gap?.dependency_control?.dependency?.asset?.name || '',
                      'Dependencia': item.gap?.dependency_control?.dependency?.name || '',
                      'Control': item.gap?.dependency_control?.control?.title || '',
                      'Tipo GAP': item.gap?.type || '',
                      'Responsable GAP': item.gap?.area?.name || '',
                      'Issue Asociado': item.issue?.type || '',
                    }))
                  }
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              columns={Columns(hasPermission, showModal, tasksTableFilter(), isSuperuser)} 
              dataSource={filteredData} 
              scroll={{ y: 450 }}
              rowKey="code"
              pagination={{
                  defaultPageSize: 30,
                  showSizeChanger: true,
                  pageSizeOptions: ['30', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
              onChange={handleTableChange}
            />
          </Spin>

          {/* Modal para Agregar/Editar */}
          <TasksModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            editingTask={editingTask}
            form={form}
            handleSave={handleSave}
            taskStatus={taskStatus}
            comments={comments}
            setComments={setComments}
            newComment={newComment}
            setNewComment={setNewComment}
            handleAddComment={handleAddComment}
            isSubmitting={isSubmitting}
            loading={loading}
          />
        </div>

        <Row style={{ marginTop: 80 }}>
          <Assetia
            pageTitle="tareas"
            pageDescription="ver y editar las tareas de manera simple"
            view="tasks"
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default Tasks;